import { keepPreviousData, useQuery, useMutation, useQueryClient, useQueries } from "@tanstack/react-query";
import { apiFetch } from "./client";
import { fetchAllPages, wantsAllPages } from "./fetchAllPages";
import type { ListResponse, SingleResponse } from "./types";

export interface DeviceGroup {
  groupId: string;
  tenantId?: string;
  name: string;
  type: string; // "static" | "dynamic"
  description?: string;
  deviceCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface GroupDeviceRow {
  imei: string;
  deviceName: string;
  status: string;
  firmwareVersion?: string;
  lastSeen?: string;
}

interface ListGroupsParams {
  limit?: number;
  cursor?: string;
  tenantId?: string;
  // false skips the live member count per group (deviceCount is then the
  // stored counter). Pickers only need names, so they pass false.
  counts?: boolean;
}

// Groups are a bounded collection used in tables and pickers. Without
// limit/cursor this loads every page, so no group goes missing. Pass
// enabled: false to hold the request until it is needed (e.g. a picker in a
// form that is not open yet).
export function useGroups(params: ListGroupsParams = {}, { enabled = true }: { enabled?: boolean } = {}) {
  const qs = new URLSearchParams();
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.counts === false) qs.set("counts", "false");
  const query = qs.toString();
  const url = `/api/v1/groups${query ? `?${query}` : ""}`;
  const init = { headers: params.tenantId ? { "X-Acting-Tenant-ID": params.tenantId } : undefined };

  return useQuery({
    queryKey: ["groups", params.tenantId ?? null, params],
    queryFn: () =>
      wantsAllPages(params) ? fetchAllPages<DeviceGroup>(url, init) : apiFetch<ListResponse<DeviceGroup>>(url, init),
    placeholderData: keepPreviousData,
    enabled,
  });
}

// Every group in one tenant, across all pages — used for group pickers,
// where a group past the first page would otherwise be unselectable.
export function useAdminGroupsForTenant(tenantId: string) {
  return useQuery({
    queryKey: ["admin", "groups", tenantId],
    queryFn: () => fetchTenantGroups(tenantId),
    enabled: !!tenantId,
    placeholderData: keepPreviousData,
  });
}

// Every group for each tenant, across all pages — used to resolve group
// names in the admin device table.
export function useAdminGroupsForTenants(tenantIds: string[]) {
  const uniqueTenantIds = Array.from(new Set(tenantIds.filter(Boolean))).sort();

  return useQueries({
    queries: uniqueTenantIds.map((tenantId) => ({
      queryKey: ["admin", "groups", tenantId],
      queryFn: () => fetchTenantGroups(tenantId),
      placeholderData: keepPreviousData,
    })),
  });
}

// Names only: these back pickers and the admin device table's group column.
function fetchTenantGroups(tenantId: string) {
  return fetchAllPages<DeviceGroup>("/api/v1/groups?counts=false", {
    headers: { "X-Acting-Tenant-ID": tenantId },
  });
}

export function useGroup(id: string) {
  return useQuery({
    queryKey: ["group", id],
    queryFn: () => apiFetch<SingleResponse<DeviceGroup>>(`/api/v1/groups/${id}`),
    enabled: !!id,
  });
}

// AddDevicesResult mirrors the backend AddDevices response: the IMEIs actually
// added, and a per-IMEI reason for any that were skipped (already in a group,
// not found, …).
export interface AddDevicesResult {
  added: string[];
  skipped?: Record<string, string>;
}

export function useAddDevicesToGroup(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (imeis: string[]) =>
      apiFetch<SingleResponse<AddDevicesResult>>(`/api/v1/groups/${groupId}/devices`, {
        method: "POST",
        body: JSON.stringify({ imeis }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["devices"] });
      qc.invalidateQueries({ queryKey: ["group", groupId] });
      qc.invalidateQueries({ queryKey: ["groups"] });
      qc.invalidateQueries({ queryKey: ["admin", "groups"] });
    },
  });
}

export function useRemoveDeviceFromGroup(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (imei: string) =>
      apiFetch<void>(`/api/v1/groups/${groupId}/devices/${encodeURIComponent(imei)}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["devices"] });
      qc.invalidateQueries({ queryKey: ["group", groupId] });
      qc.invalidateQueries({ queryKey: ["groups"] });
      qc.invalidateQueries({ queryKey: ["admin", "groups"] });
    },
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; type: string; description?: string }) =>
      apiFetch<SingleResponse<DeviceGroup>>("/api/v1/groups", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["groups"] });
      qc.invalidateQueries({ queryKey: ["admin", "groups"] });
    },
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/groups/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["groups"] });
      qc.invalidateQueries({ queryKey: ["admin", "groups"] });
    },
  });
}
